import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, HelpCircle, Info, X } from 'lucide-react';
import { ERKLAERUNG } from '../config/ebenen';
import { EinstLink } from '../pages/einstellungen/EinstLink';
import { anleitungsLink } from './hilfe/chatLogik';
import './erklaerung.css';

// Profil-Nachbau F-24 (output/profil-team-20261014/text-20261016/GRUNDREGELN.md Abschnitt 1, Benny 16.10.2026 „mach es sowie wie
// meingpt“): die drei Erklär-Bausteine nach der meinGPT-Erfassung (output/meingpt-vorlage-20261014/bilder: ki-funktionen/17 und 13,
// workspace/21 und 20, app-benutzerseite/24, teams/05, vertraege-avv/01 und 03). Seiten setzen Erklärungen nur mit diesen drei:
// - InfoTipp: Tooltip am Info-Symbol, ein Gedanke zu einem Ding (1a);
// - ErklaerFenster: Link „Wie funktioniert …?“ mit Fenster, ein Ablauf in Schritten (1b);
// - ErklaerAufklapper: „Wie funktioniert … bei Ihnen?“ an Ort und Stelle, mehrere Punkte (1c).
// E-1 Schrift mindestens 14 px; E-2 Farben nur --cl-*, kein Schatten, Radius höchstens 6 px (Rahmen, Radius und Schatten des
// Fensters kommen aus .adm-dialog); E-3 Trefferflächen 44 px, mit Maus ab 800 px sichtbar kleiner; E-4 ein eigenes natives
// <dialog> nach dem Muster von Dialog (administration/components.tsx importiert InfoTipp für Field, ein Import in die andere
// Richtung wäre ein Ring), der Aufklapper in pages/einstellungen/Bausteine.tsx bleibt für seine Verwender; E-5 HelpCircle statt
// CircleHelp (lucide-react 0.300.0). Klassen in erklaerung.css: erkl-tipp*, erkl-titelzeile, erkl-feld-kopf, erkl-fenster*,
// erkl-aufklapper*, erkl-teil*, erkl-fuss, erkl-pfeil. Alle drei tragen data-lesend (administration/readOnly.tsx): Erklärungen
// ändern nichts und bleiben in einer Sicht bedienbar. Wörter in config/ebenen.ts (ERKLAERUNG).

export type ErklaerInhalt = { frage: string; teile: { titel: string; text: React.ReactNode }[]; fuss?: React.ReactNode; hilfe?: string };

export type Rechteck = { left: number; top: number; right: number; bottom: number };
/** Oben mittig über dem Symbol, wenn dort Platz ist; sonst darunter, wenn dort Platz ist; sonst auf der Seite mit mehr Platz.
 *  Waagrecht in den Sichtbereich geklemmt (rand), bei zu breitem Kasten an den linken Rand. */
export function tippLage(anker: Rechteck, groesse: { breite: number; hoehe: number }, bereich: Rechteck, abstand = 6, rand = 8):
  { seite: 'oben' | 'unten'; left: number; top: number } {
  const platzOben = anker.top - abstand - groesse.hoehe >= bereich.top + rand;
  const platzUnten = anker.bottom + abstand + groesse.hoehe <= bereich.bottom - rand;
  const seite = platzOben ? 'oben' : platzUnten ? 'unten' : anker.top - bereich.top >= bereich.bottom - anker.bottom ? 'oben' : 'unten';
  const mitte = (anker.left + anker.right) / 2;
  const left = Math.max(bereich.left + rand, Math.min(mitte - groesse.breite / 2, bereich.right - rand - groesse.breite));
  const top = seite === 'oben' ? anker.top - abstand - groesse.hoehe : anker.bottom + abstand;
  return { seite, left: Math.round(left), top: Math.round(top) };
}

/** Der sichtbare Bereich eines Elements: das Fenster (documentElement.clientWidth/-Height), geschnitten mit jedem Vorfahren, der
 *  rollt (overflow auto oder scroll, etwa der rollende Inhalt einer Bereichsseite unter dem klebenden Seitenkopf, ein offenes
 *  <dialog>). overflow hidden oder clip ohne Rollen (etwa .ki-kennzahlen im KI-Cockpit) beschneidet den fixierten Kasten nicht und
 *  zählt nicht. */
export function sichtbereich(element: Element): Rechteck {
  const wurzel = document.documentElement;
  let bereich: Rechteck = { left: 0, top: 0, right: wurzel.clientWidth, bottom: wurzel.clientHeight };
  // body und html geben ihr overflow an das Fenster weiter; sie zählen schon als Fenster.
  for (let vorfahr = element.parentElement; vorfahr && vorfahr !== document.body && vorfahr !== wurzel; vorfahr = vorfahr.parentElement) {
    const stil = getComputedStyle(vorfahr);
    if (!/auto|scroll/.test(`${stil.overflowX} ${stil.overflowY}`)) continue;
    const r = vorfahr.getBoundingClientRect();
    bereich = { left: Math.max(bereich.left, r.left), top: Math.max(bereich.top, r.top), right: Math.min(bereich.right, r.right), bottom: Math.min(bereich.bottom, r.bottom) };
  }
  return bereich;
}

/** Zoom, mit dem ein Element gezeichnet wird (F-27: Inhalt des Fensters „Einstellungen“ mit zoom). getBoundingClientRect liefert
 *  Bildschirm-px, offsetWidth und style.left/top gelten in den px des Elements; Bildschirm-px durch diesen Wert sind Element-px. */
export function zoomVon(element: Element): number {
  return (element as Element & { currentCSSZoom?: number }).currentCSSZoom || 1;
}

// Nur ein Tooltip zugleich: die Funktion, die den offenen schließt.
let offenerTipp: (() => void) | null = null;

/** Tooltip am Info-Symbol (GRUNDREGELN 1a): ein bis zwei Sätze, kein Link, kein Knopf. Öffnet bei Maus nach 200 ms, bei
 *  Tastaturfokus und per Antippen; Escape, Maus weg, Fokus weg, ein Klick daneben, Scrollen und Größenänderung schließen. Der
 *  Kasten steht immer im Dokument (geschlossen hidden), damit aria-describedby den Satz beim Fokus vorlesen lässt. Lage fixiert am
 *  Symbol und um den Versatz eines Container-Vorfahren berichtigt (wie Zeilenmenue in pages/einstellungen/Bausteine.tsx).
 *  Der Auslöser ist ein span mit role="button" statt eines <button>: ein gesperrtes <fieldset> sperrt jeden <button> darin mit
 *  (kein Fokus, kein Klick), die Erklärung bleibt aber auch dort bedienbar (E-4; Schlusslauf 4, CostPanel). */
export function InfoTipp({ wort, text }: { wort: string; text: React.ReactNode }) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLSpanElement>(null);
  const kasten = useRef<HTMLSpanElement>(null);
  const lage = useRef<DOMRect | null>(null);
  const zeiger = useRef('');
  const verweilen = useRef<number | undefined>(undefined);
  const zu = useRef(() => setOffen(false)).current;
  const id = useId();
  const warteStopp = () => { window.clearTimeout(verweilen.current); verweilen.current = undefined; };

  useLayoutEffect(() => {
    const k = knopf.current, l = kasten.current;
    if (!offen || !k || !l) return;
    l.style.left = '0px'; l.style.top = '0px';
    const basis = l.getBoundingClientRect();
    lage.current = k.getBoundingClientRect();
    const anker = (k.querySelector('svg') ?? k).getBoundingClientRect();
    const z = zoomVon(l);
    const ziel = tippLage(anker, { breite: l.offsetWidth * z, hoehe: l.offsetHeight * z }, sichtbereich(k));
    l.style.left = `${Math.round((ziel.left - basis.left) / z)}px`; l.style.top = `${Math.round((ziel.top - basis.top) / z)}px`;
    l.dataset.seite = ziel.seite;
  }, [offen]);

  useEffect(() => {
    if (!offen) return;
    if (offenerTipp && offenerTipp !== zu) offenerTipp();
    offenerTipp = zu;
    // Escape schließt nur den Tooltip, weder die Bereichsseite noch das Pop-up noch ein <dialog> darum; erst das nächste wirkt wie bisher.
    const taste = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation();
      setOffen(false);
    };
    const aussen = (event: PointerEvent) => { if (!knopf.current?.contains(event.target as Node)) setOffen(false); };
    // Nur ein Scrollen, das den Knopf gegenüber dem Öffnen um mehr als 1 px verschoben hat, schließt (wie Zeilenmenue).
    const gescrollt = () => {
      const vorher = lage.current, jetzt = knopf.current?.getBoundingClientRect();
      if (!vorher || !jetzt || Math.abs(jetzt.top - vorher.top) > 1 || Math.abs(jetzt.left - vorher.left) > 1) setOffen(false);
    };
    document.addEventListener('keydown', taste, true);
    document.addEventListener('pointerdown', aussen, true);
    window.addEventListener('scroll', gescrollt, true);
    window.addEventListener('resize', zu);
    return () => {
      document.removeEventListener('keydown', taste, true);
      document.removeEventListener('pointerdown', aussen, true);
      window.removeEventListener('scroll', gescrollt, true);
      window.removeEventListener('resize', zu);
      if (offenerTipp === zu) offenerTipp = null;
    };
  }, [offen, zu]);

  useEffect(() => () => window.clearTimeout(verweilen.current), []);

  // Ein Klick nach touch oder pen schaltet um; nach mouse tut er nichts (die Maus hat schon geöffnet); ohne pointerdown kommt
  // er von Enter oder Leertaste (oder einem Bildschirmleser) und öffnet.
  const klick = () => {
    const art = zeiger.current;
    zeiger.current = '';
    if (art === 'touch' || art === 'pen') setOffen(wert => !wert);
    else if (art !== 'mouse') setOffen(true);
  };
  // Enter und Leertaste wirken wie bei einem Knopf. preventDefault hält ein umgebendes label oder summary still (ein <button> war
  // interaktiver Inhalt) und die Seite bei der Leertaste ruhig.
  return <span className="erkl-tipp" data-lesend="">
    <span ref={knopf} role="button" tabIndex={0} className="erkl-tipp-knopf" aria-label={ERKLAERUNG.tipp(wort)} aria-describedby={id}
      data-offen={offen ? '' : undefined}
      onPointerEnter={event => { if (event.pointerType !== 'mouse') return; warteStopp(); verweilen.current = window.setTimeout(() => setOffen(true), 200); }}
      onPointerLeave={event => { if (event.pointerType !== 'mouse') return; warteStopp(); setOffen(false); }}
      onPointerDown={event => { zeiger.current = event.pointerType; }}
      onKeyDown={event => { if (event.key !== 'Enter' && event.key !== ' ') return; event.preventDefault(); zeiger.current = ''; klick(); }}
      onFocus={event => { if (event.currentTarget.matches(':focus-visible')) setOffen(true); }}
      onBlur={() => { warteStopp(); setOffen(false); }}
      onClick={event => { event.preventDefault(); klick(); }}><Info size={14} aria-hidden="true" /></span>
    <span ref={kasten} id={id} role="tooltip" className="erkl-tipp-kasten" hidden={!offen}>{text}</span>
  </span>;
}

/** Link „Wie funktioniert …?“ mit Fenster (GRUNDREGELN 1b): rechts im Kopf eines Abschnitts oder einer Seite, ein leiser
 *  Textlink. Das Fenster speichert nichts und ruft keinen Server. */
export function ErklaerFenster({ frage, teile, fuss, hilfe }: ErklaerInhalt) {
  const [offen, setOffen] = useState(false);
  return <span className="erkl-fenster-ort" data-lesend="">
    <button type="button" className="erkl-fenster-link" aria-haspopup="dialog" onClick={() => setOffen(true)}>
      <HelpCircle size={14} aria-hidden="true" /><span>{frage}</span></button>
    {offen && <Fenster frage={frage} teile={teile} fuss={fuss} hilfe={hilfe} onClose={() => setOffen(false)} />}
  </span>;
}

// Ein natives Modal nach dem Muster von Dialog (administration/components.tsx): showModal() beim Einhängen, Fokus ins Fenster und
// dort gefangen, beim Abbau zurück auf den Link. Escape (onCancel), das X und ein Klick daneben schließen; SeitenRahmen und das
// Pop-up erkennen dialog[open] und lassen Escape dem Fenster. Ein Klick daneben zählt nur, wenn Drücken und Loslassen beide
// außerhalb des Fensters lagen; ein Klick in den Rand innerhalb schließt nicht.
function Fenster({ frage, teile, fuss, hilfe, onClose }: ErklaerInhalt & { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titelId = useId();
  const gedrueckt = useRef(false);
  useEffect(() => {
    const vorher = document.activeElement as HTMLElement | null;
    const dialog = ref.current!;
    dialog.showModal();
    return () => { dialog.close(); vorher?.focus(); };
  }, []);
  const ausserhalb = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target !== event.currentTarget) return false;
    const r = event.currentTarget.getBoundingClientRect();
    return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
  };
  return <dialog ref={ref} className="adm-dialog erkl-fenster" aria-labelledby={titelId} onCancel={event => { event.preventDefault(); onClose(); }}
    onPointerDown={event => { gedrueckt.current = ausserhalb(event); }}
    onClick={event => { const daneben = gedrueckt.current && ausserhalb(event); gedrueckt.current = false; if (daneben) onClose(); }}>
    <header><h2 id={titelId}>{frage}</h2>
      <button type="button" className="erkl-fenster-zu" aria-label={ERKLAERUNG.schliessen} onClick={onClose}><X size={16} aria-hidden="true" /></button></header>
    <div className="erkl-fenster-teile">{teile.map((teil, i) => <section key={i} className="erkl-teil"><h3 className="erkl-teil-titel">{teil.titel}</h3><div className="erkl-teil-text">{teil.text}</div></section>)}</div>
    {fuss && <div className="erkl-fuss">{fuss}</div>}
    {/* Der Weg in die Hilfe schließt zuerst das Fenster (onClick läuft in EinstLink vor der Navigation); fragt die Wache nach
        ungespeicherten Eingaben, steht die Rückfrage nicht hinter einem Modal. */}
    {hilfe && <p className="erkl-fenster-hilfe"><EinstLink to={anleitungsLink(hilfe)} onClick={onClose}>{ERKLAERUNG.mehrInDerHilfe}</EinstLink></p>}
  </dialog>;
}

/** Aufklapper „Wie funktioniert … bei Ihnen?“ an Ort und Stelle (GRUNDREGELN 1c): umrandete Kopfzeile, zu Beginn zugeklappt,
 *  ein echter Knopf mit aria-expanded und aria-controls. Er merkt sich nichts. Die Teil-Titel stehen in normaler Schreibung; die
 *  Großbuchstaben macht nur CSS. h3, weil er als Kind im Abschnitt (h2) steht. */
export function ErklaerAufklapper({ frage, teile, fuss }: Omit<ErklaerInhalt, 'hilfe'>) {
  const [offen, setOffen] = useState(false);
  const id = useId();
  return <div className="erkl-aufklapper" data-lesend="">
    <button type="button" className="erkl-aufklapper-kopf" aria-expanded={offen} aria-controls={id} onClick={() => setOffen(wert => !wert)}>
      <HelpCircle size={14} aria-hidden="true" /><span>{frage}</span><ChevronDown size={16} aria-hidden="true" className="erkl-pfeil" /></button>
    <div id={id} className="erkl-aufklapper-inhalt" hidden={!offen}>
      {teile.map((teil, i) => <div key={i} className="erkl-teil"><h3 className="erkl-teil-titel">{teil.titel}</h3><div className="erkl-teil-text">{teil.text}</div></div>)}
      {fuss && <div className="erkl-fuss">{fuss}</div>}
    </div>
  </div>;
}
